const FALLBACK_DURATION_MS = 1250

function finiteNumber(value, fallback = 0) {
  const number = Number(value)
  return Number.isFinite(number) ? number : fallback
}

function frameDuration(frame) {
  const duration = finiteNumber(frame?.durationMs, FALLBACK_DURATION_MS)
  return duration > 0 ? duration : FALLBACK_DURATION_MS
}

export function buildFrameTimeline(frames = []) {
  let cursor = 0
  const entries = frames.map((frame, index) => {
    const durationMs = frameDuration(frame)
    const explicitStart = finiteNumber(frame?.startMs, NaN)
    const startMs = Number.isFinite(explicitStart) ? explicitStart : cursor
    const entry = { frame, index, startMs, durationMs, endMs: startMs + durationMs }
    cursor = entry.endMs
    return entry
  })
  const totalMs = entries.length ? entries[entries.length - 1].endMs : 0
  return { entries, totalMs }
}

export function clampGlobalMs(timeline, globalMs) {
  const total = finiteNumber(timeline?.totalMs, 0)
  return Math.max(0, Math.min(total, finiteNumber(globalMs, 0)))
}

export function frameAtGlobalMs(timeline, globalMs, fallbackIndex = 0) {
  const entries = timeline?.entries || []
  if (!entries.length) return null

  const ms = clampGlobalMs(timeline, globalMs)
  let index = Math.max(0, Math.min(fallbackIndex || 0, entries.length - 1))
  while (index + 1 < entries.length && entries[index + 1].startMs <= ms) index += 1
  while (index > 0 && entries[index].startMs > ms) index -= 1
  return entries[index]
}

export function globalMsForFrame(timeline, frameIndex, inFrameMs = 0) {
  const entry = timeline?.entries?.[frameIndex]
  if (!entry) return 0
  const offset = Math.max(0, Math.min(entry.durationMs, finiteNumber(inFrameMs, 0)))
  return entry.startMs + offset
}

export function positionForGlobalMs(timeline, globalMs, fallbackIndex = 0) {
  const entry = frameAtGlobalMs(timeline, globalMs, fallbackIndex)
  if (!entry) return { index: 0, inFrameMs: 0, progress: 0, globalMs: 0 }
  const clampedGlobalMs = clampGlobalMs(timeline, globalMs)
  const inFrameMs = Math.max(0, Math.min(entry.durationMs, clampedGlobalMs - entry.startMs))
  return {
    index: entry.index,
    inFrameMs,
    progress: entry.durationMs > 0 ? inFrameMs / entry.durationMs : 0,
    globalMs: entry.startMs + inFrameMs,
  }
}

export function globalMsForStripX(timeline, metrics, localX) {
  const offsets = metrics?.cellOffsets || []
  const widths = metrics?.cellWidths || []
  const entries = timeline?.entries || []
  if (!entries.length || !offsets.length) return 0

  const x = finiteNumber(localX, offsets[0] || 0)
  let index = 0
  for (let i = 0; i < offsets.length; i += 1) {
    if (x >= offsets[i]) index = i
    else break
  }
  index = Math.max(0, Math.min(index, entries.length - 1))
  const width = Math.max(1, finiteNumber(widths[index], 1))
  const progress = Math.max(0, Math.min(1, (x - finiteNumber(offsets[index], 0)) / width))
  return globalMsForFrame(timeline, index, progress * entries[index].durationMs)
}

export function stripXForGlobalMs(timeline, metrics, globalMs, fallbackIndex = 0) {
  const offsets = metrics?.cellOffsets || []
  const widths = metrics?.cellWidths || []
  const position = positionForGlobalMs(timeline, globalMs, fallbackIndex)
  const offset = finiteNumber(offsets[position.index], offsets[0] || 0)
  const width = finiteNumber(widths[position.index], 1)
  return offset + (position.progress * width)
}
